import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { debounce } from '@theme/utilities';

/* ── Configurazione ────────────────────────────── */
const GIFT_VARIANT_ID = 65797306909021; // Bottiglia Mignon Omaggio
const THRESHOLD_CENTS = 8000; // €80,00
const SOURCE_TAG = 'auto-gift-script';

let isProcessing = false;

/**
 * Calcola il subtotale del carrello escludendo la riga del regalo,
 * così il regalo stesso non influisce sulla soglia.
 */
function getSubtotalExcludingGift(cart) {
  return cart.items
    .filter((item) => item.variant_id !== GIFT_VARIANT_ID)
    .reduce((sum, item) => sum + item.original_line_price, 0);
}

/**
 * Trova la riga del regalo nel carrello (oggetto completo, non solo l'id),
 * ci serve la sua "key" per poterla rimuovere correttamente.
 */
function findGiftLine(cart) {
  return cart.items.find((item) => item.variant_id === GIFT_VARIANT_ID);
}

async function getCart() {
  const response = await fetch('/cart.js');
  return response.json();
}

async function addGift() {
  const response = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      items: [
        {
          id: GIFT_VARIANT_ID,
          quantity: 1,
          properties: { _omaggio_auto: 'true' },
        },
      ],
    }),
  });
  return response.json();
}

/**
 * Rimuove il regalo dal carrello.
 * IMPORTANTE: usa la "key" completa della riga (variant_id + hash delle
 * proprietà), non il variant_id nudo — altrimenti Shopify risponde 400
 * perché la riga ha proprietà custom (_omaggio_auto) che la distinguono.
 */
async function removeGift(giftLine) {
  const response = await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      id: giftLine.key,
      quantity: 0,
    }),
  });
  return response.json();
}

async function evaluateCart() {
  if (isProcessing) return;

  try {
    isProcessing = true;
    const cart = await getCart();
    const subtotal = getSubtotalExcludingGift(cart);
    const giftLine = findGiftLine(cart);

    let updatedCart = null;

    if (subtotal >= THRESHOLD_CENTS && !giftLine) {
      updatedCart = await addGift();
    } else if (subtotal < THRESHOLD_CENTS && giftLine) {
      updatedCart = await removeGift(giftLine);
    }

    if (updatedCart) {
      document.dispatchEvent(
        new CartUpdateEvent(updatedCart, SOURCE_TAG, {
          source: SOURCE_TAG,
          itemCount: updatedCart.item_count,
        })
      );
    }
  } catch (error) {
    console.error('[auto-gift] Errore aggiornamento omaggio:', error);
  } finally {
    isProcessing = false;
  }
}

const debouncedEvaluate = debounce(evaluateCart, 300);

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  // Evita di rielaborare l'evento che lo script stesso ha generato
  if (event.detail?.data?.source === SOURCE_TAG) return;
  debouncedEvaluate();
});

// Controllo iniziale al caricamento pagina (es. arrivo da link diretto con carrello già pieno)
document.addEventListener('DOMContentLoaded', evaluateCart, { once: true });